/**
 * Databases (Notion-like): filters, multi-key sorts, grouping, fractional ranks (board drag), calendar month
 * placement, cell display descriptors and the cache reducer for row/view/database events.
 *
 * Pure: "now", the time zone, my user id and member names come in through `DbContext`. Rows are kept in rank
 * order (plain string comparison of fractional-indexing keys); views never mutate the cached rows.
 */
import type {
  Calculation,
  CreateViewRequest,
  Database,
  DbView,
  FilterCondition,
  FilterOperator,
  FormulaErrorCode,
  FormulaResult,
  FormulaResultKind,
  OptionColor,
  PersistentEvent,
  PropertyDef,
  PropertyType,
  PropertyValue,
  Row,
  SelectOption,
  StatusGroup,
  StatusOption,
  UpdateDatabaseRequest,
  UpdateViewRequest,
  ViewFilter,
  ViewLayout,
  ViewSort,
  RollupDef,
} from "@bundle/contracts";
import { generateKeyBetween } from "fractional-indexing";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";
import { civilFromDays, localDay } from "./timeline.ts";

/** What filtering, sorting and grouping need besides the rows. */
export type DbContext = {
  meId: string;
  /** Epoch ms (injected clock). */
  now: number;
  /** Minutes to add to UTC for local time (UTC-3 → -180). */
  tzOffsetMinutes: number;
  /** userId → display name (person sorting and group order). */
  names: Record<string, string>;
};

/** A database as cached on the client: schema, views (rank order) and rows (rank order). */
export type DatabaseCache = { database: Database; views: DbView[]; rows: Row[] };

/** A row to create (optimistic): it exists under the temporary id `clientId` until the server answers. */
export type NewRowInput = {
  clientId: string;
  values: Record<string, PropertyValue>;
  rank: string;
  /** Server placement (CreateRowRequest.afterId); absent ⇒ last. */
  afterId?: string;
  at: number;
  /** A row template (`values` already include its defaults; the server copies its body). */
  templateId?: string;
};

/** A partial row update (UpdateRowRequest + the optimistic rank). */
export type RowPatch = {
  values?: Record<string, PropertyValue | null>;
  rank?: string;
  afterId?: string;
  beforeId?: string;
  icon?: string;
  at: number;
};

/** A view to create (optimistic, temporary id `clientId`). */
export type NewViewInput = { clientId: string; rank: string; request: CreateViewRequest };

/** Group key of rows without a value. */
export const NONE_GROUP = "none";

const DAY_MS = 86_400_000;
const STATUS_GROUP_ORDER: StatusGroup[] = ["todo", "in_progress", "complete"];
/** Colours given to new options, in order (then repeating). */
export const OPTION_COLORS: OptionColor[] = ["blue", "green", "yellow", "orange", "red", "purple", "pink", "brown", "gray", "default"];

// ---------- Dates ----------

/** A date in local days since 1970-01-01, with minutes since local midnight when it has a time. */
export type DayTime = { day: number; minutes?: number };

/** Day number of a civil date (Howard Hinnant's days_from_civil). */
export function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const mp = month > 2 ? month - 3 : month + 9;
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146_097 + doe - 719_468;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Parses "YYYY-MM-DD" (all-day) or an ISO date-time. Date-times with an offset (or Z) are converted to local time
 * with `tzOffsetMinutes`; date-times without one are floating (taken as local). Invalid strings → undefined.
 */
export function parseDate(s: string, tzOffsetMinutes: number): DayTime | undefined {
  const m = DATE_RE.exec(s.trim());
  if (m === null) return undefined;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const days = daysFromCivil(year, month, day);
  if (m[4] === undefined) return { day: days };
  const minutesOfDay = Number(m[4]) * 60 + Number(m[5]);
  const zone = m[7];
  if (zone === undefined) return { day: days, minutes: minutesOfDay };
  let offset = 0;
  if (zone !== "Z") {
    const sign = zone.startsWith("-") ? -1 : 1;
    const digits = zone.slice(1).replace(":", "");
    offset = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)));
  }
  const localMinutes = days * 1440 + minutesOfDay - offset + tzOffsetMinutes;
  const localDayNumber = Math.floor(localMinutes / 1440);
  return { day: localDayNumber, minutes: localMinutes - localDayNumber * 1440 };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "YYYY-MM-DD" for a day number. */
export function formatDay(day: number): string {
  const c = civilFromDays(day);
  return `${c.year}-${pad2(c.month)}-${pad2(c.day)}`;
}

/** Today's local day number. */
export function today(ctx: DbContext): number {
  return localDay(ctx.now, ctx.tzOffsetMinutes);
}

function timestampDay(ts: number, tzOffsetMinutes: number): DayTime {
  const local = ts + tzOffsetMinutes * 60_000;
  const day = Math.floor(local / DAY_MS);
  return { day, minutes: Math.floor((local - day * DAY_MS) / 60_000) };
}

// ---------- Schema helpers ----------

export function propertyById(db: Database, propertyId: string): PropertyDef | undefined {
  return db.properties.find((p) => p.id === propertyId);
}

export function titleProperty(db: Database): PropertyDef | undefined {
  return db.properties.find((p) => p.type === "title");
}

/** The row's title ("" when untitled). */
export function rowTitle(db: Database, row: Row): string {
  const p = titleProperty(db);
  if (p === undefined) return "";
  const v = row.values[p.id];
  return v !== undefined && v.type === "title" ? v.text : "";
}

/** Properties a board/list/table can group by. */
export function isGroupable(type: PropertyType): boolean {
  return type === "select" || type === "status" || type === "person" || type === "checkbox";
}

export function groupableProperties(db: Database): PropertyDef[] {
  return db.properties.filter((p) => isGroupableProperty(p));
}

export function isDateType(type: PropertyType): boolean {
  return type === "date" || type === "created_time" || type === "edited_time";
}

export function dateProperties(db: Database): PropertyDef[] {
  return db.properties.filter((p) => p.type === "date");
}

/** Server-computed types the user can't edit. */
export function isReadOnlyType(type: PropertyType): boolean {
  return type === "created_time" || type === "created_by" || type === "edited_time" || type === "edited_by" || type === "formula" || type === "rollup";
}

/** Formula and rollup properties: their cells hold a server-calculated result. */
export function isCalculatedType(type: PropertyType): boolean {
  return type === "formula" || type === "rollup";
}

/**
 * The kind of value a formula or rollup produces: the server's `resultKind`, else what the rollup calculation
 * implies, else text. Undefined for other types.
 */
export function resultKind(prop: PropertyDef): FormulaResultKind | undefined {
  if (prop.type === "formula") return prop.formula?.resultKind ?? (prop.formula?.format === "date" ? "date" : prop.formula?.format !== undefined ? "number" : "text");
  if (prop.type !== "rollup") return undefined;
  if (prop.rollup?.resultKind !== undefined) return prop.rollup.resultKind;
  const c = prop.rollup?.calculate;
  if (c === undefined || c === "show_original") return "text";
  if (c === "earliest" || c === "latest") return "date";
  return "number";
}

/**
 * The type whose filters, sorting, grouping and calculations a property follows: a formula or rollup behaves like
 * the type of its result (number, text, checkbox, date); other properties are their own type.
 */
export function behavesAs(prop: PropertyDef): PropertyType {
  switch (resultKind(prop)) {
    case "number":
      return "number";
    case "boolean":
      return "checkbox";
    case "date":
      return "date";
    case "text":
      return "text";
    case undefined:
      return prop.type;
  }
}

/** Groupable, formulas and rollups included (by text value, checked/unchecked or day; numbers aren't). */
export function isGroupableProperty(prop: PropertyDef): boolean {
  if (isCalculatedType(prop.type)) {
    const k = resultKind(prop);
    return k === "text" || k === "boolean" || k === "date";
  }
  return isGroupable(prop.type);
}

/** Visible non-title properties of a view, in view order (unknown ids skipped). */
export function visibleProperties(db: Database, view: DbView): PropertyDef[] {
  const out: PropertyDef[] = [];
  for (const id of view.visiblePropertyIds) {
    const p = propertyById(db, id);
    if (p !== undefined && p.type !== "title" && !out.includes(p)) out.push(p);
  }
  return out;
}

/** Non-title properties hidden in a view, in schema order. */
export function hiddenProperties(db: Database, view: DbView): PropertyDef[] {
  return db.properties.filter((p) => p.type !== "title" && !view.visiblePropertyIds.includes(p.id));
}

/** Shows (appends) or hides a property. */
export function setPropertyVisible(ids: string[], propertyId: string, visible: boolean): string[] {
  const without = ids.filter((id) => id !== propertyId);
  return visible ? [...without, propertyId] : without;
}

/** Moves a visible property to `toIndex` (clamped). */
export function moveVisibleProperty(ids: string[], propertyId: string, toIndex: number): string[] {
  if (!ids.includes(propertyId)) return ids;
  const without = ids.filter((id) => id !== propertyId);
  const at = Math.max(0, Math.min(toIndex, without.length));
  return [...without.slice(0, at), propertyId, ...without.slice(at)];
}

/** Next colour for a new option (cycles through OPTION_COLORS by option count). */
export function nextOptionColor(options: SelectOption[]): OptionColor {
  return OPTION_COLORS[options.length % OPTION_COLORS.length]!;
}

/** Status options in board order: To-do, In progress, Complete (option order kept within a group). */
export function statusOptionsInOrder(options: StatusOption[]): StatusOption[] {
  const out: StatusOption[] = [];
  for (const g of STATUS_GROUP_ORDER) for (const o of options) if (o.group === g) out.push(o);
  return out;
}

/**
 * The definition after changing a property's type. Select ↔ multi-select keep their options; select/multi →
 * status keep them in "To-do"; status → select/multi keep the status options; `defaultStatus` seeds a status
 * property that had no options. Number keeps its format. Other settings are dropped.
 */
export function convertProperty(def: PropertyDef, type: PropertyType, defaultStatus: StatusOption[]): PropertyDef {
  const out: PropertyDef = { id: def.id, name: def.name, type };
  const selectLike = def.options ?? (def.statusOptions ?? []).map((o) => ({ id: o.id, name: o.name, color: o.color }));
  if (type === "select" || type === "multi_select") out.options = selectLike;
  if (type === "status") {
    if (def.statusOptions !== undefined && def.statusOptions.length > 0) out.statusOptions = def.statusOptions;
    else if (def.options !== undefined && def.options.length > 0) out.statusOptions = def.options.map((o) => ({ id: o.id, name: o.name, color: o.color, group: "todo" as const }));
    else out.statusOptions = defaultStatus;
  }
  if (type === "number") out.numberFormat = def.numberFormat ?? "number";
  if (type === "relation" && def.relation !== undefined) out.relation = def.relation;
  if (type === "formula") out.formula = def.formula ?? { expression: "" };
  if (type === "rollup" && def.rollup !== undefined) out.rollup = def.rollup;
  return out;
}

// ---------- Cell access ----------

/**
 * The stored value when it matches the property's type (values left over from a type change read as empty). A cell
 * formula (Sheet view) reads as the typed value of its result (`formulaCellValue`), so sorting, filtering, grouping
 * and calculations see what the cell shows.
 */
export function cellValue(row: Row, prop: PropertyDef): PropertyValue | undefined {
  const v = row.values[prop.id];
  if (v !== undefined && v.type === "cell_formula") return formulaCellValue(prop, v.result);
  return v !== undefined && v.type === prop.type ? v : undefined;
}

// ---------- Cell formulas (Sheet view; docs/specs/database-formulas.md § Phase 2) ----------

const PROPERTY_NOUN: Partial<Record<PropertyType, string>> = {
  number: "a number", checkbox: "TRUE or FALSE", date: "a date", select: "one of its options", status: "one of its statuses",
  multi_select: "its options", person: "a person", relation: "a page", files: "a file",
};

/**
 * A cell formula's engine result made to fit its column (typed columns stay typed): number columns take numbers;
 * text, title, url and email anything (shown as text); dates take dates (a number is a date serial); checkboxes
 * TRUE/FALSE; select/status an option name, multi-select names joined with ", " (case-insensitive). Anything else is
 * `#VALUE!` "<Prop> expects a <type>". Errors pass through; an empty result is empty text.
 */
export function cellFormulaResult(prop: PropertyDef, result: FormulaResult | undefined): FormulaResult {
  const r: FormulaResult = result ?? { kind: "text", text: "" };
  if (r.kind === "error") return r;
  const bad: FormulaResult = { kind: "error", error: "#VALUE!", message: `${prop.name} expects ${PROPERTY_NOUN[prop.type] ?? `a ${prop.type}`}` };
  const empty = r.kind === "text" && r.text === "";
  switch (prop.type) {
    case "title":
    case "text":
    case "url":
    case "email":
      return r;
    case "number":
      return r.kind === "number" || empty ? r : bad;
    case "checkbox":
      return r.kind === "boolean" || empty ? r : bad;
    case "date":
      if (r.kind === "date" || empty) return r;
      if (r.kind === "number" && Number.isFinite(r.number)) return { kind: "date", date: { start: formatDay(Math.floor(r.number) - 25_569) } };
      return bad;
    case "select":
    case "status":
    case "multi_select": {
      if (empty) return r;
      const options: SelectOption[] = prop.type === "status" ? (prop.statusOptions ?? []) : (prop.options ?? []);
      const names = prop.type === "multi_select" ? resultText(r).split(",").map((x) => x.trim()).filter((x) => x !== "") : [resultText(r).trim()];
      const ok = names.length > 0 && names.every((n) => options.some((o) => o.name.toLowerCase() === n.toLowerCase()));
      return ok ? { kind: "text", text: resultText(r) } : bad;
    }
    default:
      return bad;
  }
}

/**
 * The typed value a cell formula's result stands for in its column (undefined when empty or an error): a number,
 * text (title/text/url/email), date, checkbox, or the options named by the text. Other columns read as empty.
 */
export function formulaCellValue(prop: PropertyDef, result: FormulaResult): PropertyValue | undefined {
  if (result.kind === "error") return undefined;
  const text = resultText(result);
  switch (prop.type) {
    case "title":
    case "text":
      return text === "" ? undefined : { type: prop.type, text };
    case "url":
      return text === "" ? undefined : { type: "url", url: text };
    case "email":
      return text === "" ? undefined : { type: "email", email: text };
    case "number":
      return result.kind === "number" ? { type: "number", number: result.number } : undefined;
    case "checkbox":
      return result.kind === "boolean" ? { type: "checkbox", checked: result.value } : undefined;
    case "date":
      return result.kind === "date" ? { type: "date", date: result.date } : undefined;
    case "select":
    case "status": {
      const options: SelectOption[] = prop.type === "status" ? (prop.statusOptions ?? []) : (prop.options ?? []);
      const o = options.find((x) => x.name.toLowerCase() === text.trim().toLowerCase());
      return o === undefined || text === "" ? undefined : { type: prop.type, optionId: o.id };
    }
    case "multi_select": {
      const ids: string[] = [];
      for (const n of text.split(",").map((x) => x.trim()).filter((x) => x !== "")) {
        const o = (prop.options ?? []).find((x) => x.name.toLowerCase() === n.toLowerCase());
        if (o !== undefined && !ids.includes(o.id)) ids.push(o.id);
      }
      return ids.length === 0 ? undefined : { type: "multi_select", optionIds: ids };
    }
    default:
      return undefined;
  }
}

/**
 * What someone typed or pasted into a cell, as a value for its column: null clears (blank text), undefined when it
 * doesn't fit (the caller keeps it out, or reports it). Text starting with "=" is a formula (not handled here).
 * Numbers accept `1,234.5`, `12%` (0.12) and a leading `$`; checkboxes TRUE/FALSE/yes/no/1/0/✓; dates YYYY-MM-DD (or
 * an ISO date-time); select/status/multi-select by option name (multi: comma-separated). Computed columns,
 * people, relations and files don't take text.
 */
export function cellInputValue(prop: PropertyDef, text: string): PropertyValue | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  switch (prop.type) {
    case "title":
    case "text":
      return { type: prop.type, text };
    case "url":
      return { type: "url", url: t };
    case "email":
      return { type: "email", email: t };
    case "number": {
      let s = t.replace(/,/g, "").replace(/^\$/, "");
      const percent = s.endsWith("%");
      if (percent) s = s.slice(0, -1);
      if (!/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(s)) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? { type: "number", number: percent ? Number((n / 100).toPrecision(15)) : n } : undefined;
    }
    case "checkbox": {
      const l = t.toLowerCase();
      if (["true", "yes", "1", "✓", "x"].includes(l)) return { type: "checkbox", checked: true };
      if (["false", "no", "0"].includes(l)) return { type: "checkbox", checked: false };
      return undefined;
    }
    case "date": {
      if (!/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/.test(t) || Number.isNaN(Date.parse(t))) return undefined;
      return { type: "date", date: { start: t } };
    }
    case "select":
    case "status": {
      const options: SelectOption[] = prop.type === "status" ? (prop.statusOptions ?? []) : (prop.options ?? []);
      const o = options.find((x) => x.name.toLowerCase() === t.toLowerCase());
      return o === undefined ? undefined : { type: prop.type, optionId: o.id };
    }
    case "multi_select": {
      const ids: string[] = [];
      for (const n of t.split(",").map((x) => x.trim()).filter((x) => x !== "")) {
        const o = (prop.options ?? []).find((x) => x.name.toLowerCase() === n.toLowerCase());
        if (o === undefined) return undefined;
        if (!ids.includes(o.id)) ids.push(o.id);
      }
      return ids.length === 0 ? null : { type: "multi_select", optionIds: ids };
    }
    default:
      return undefined;
  }
}

/** Text of title/text/url/email cells ("" when empty or another type). */
export function cellText(row: Row, prop: PropertyDef): string {
  const v = cellValue(row, prop);
  if (v === undefined) return "";
  if (v.type === "title" || v.type === "text") return v.text;
  if (v.type === "url") return v.url;
  if (v.type === "email") return v.email;
  return "";
}

/** Ids held by select/status/multi-select/person/relation/files cells (and created_by/edited_by). */
export function cellIds(row: Row, prop: PropertyDef): string[] {
  if (prop.type === "created_by") return [row.createdBy];
  if (prop.type === "edited_by") return [row.updatedBy];
  const v = cellValue(row, prop);
  if (v === undefined) return [];
  switch (v.type) {
    case "select":
    case "status":
      return [v.optionId];
    case "multi_select":
      return v.optionIds;
    case "person":
      return v.userIds;
    case "relation":
      return v.pageIds;
    case "contact":
      return v.contactIds;
    case "files":
      return v.fileIds;
    default:
      return [];
  }
}

/** The result of a formula or rollup cell, or of a cell formula (undefined when empty or another type). */
export function cellResult(row: Row, prop: PropertyDef): FormulaResult | undefined {
  const raw = row.values[prop.id];
  if (raw !== undefined && raw.type === "cell_formula") return raw.result;
  const v = cellValue(row, prop);
  return v !== undefined && (v.type === "formula" || v.type === "rollup") ? v.result : undefined;
}

/** A result as plain text (numbers unformatted, booleans TRUE/FALSE, dates as stored, errors as their code). */
export function resultText(r: FormulaResult | undefined): string {
  if (r === undefined) return "";
  switch (r.kind) {
    case "number":
      return String(r.number);
    case "text":
      return r.text;
    case "boolean":
      return r.value ? "TRUE" : "FALSE";
    case "date":
      return r.date.start;
    case "error":
      return r.error;
  }
}

function cellNumber(row: Row, prop: PropertyDef): number | undefined {
  const v = cellValue(row, prop);
  if (v !== undefined && v.type === "number") return v.number;
  const r = cellResult(row, prop);
  return r !== undefined && r.kind === "number" ? r.number : undefined;
}

function cellChecked(row: Row, prop: PropertyDef): boolean {
  const v = cellValue(row, prop);
  if (v !== undefined && v.type === "checkbox") return v.checked;
  const r = cellResult(row, prop);
  return r !== undefined && r.kind === "boolean" && r.value;
}

/** Start (and end) of a date cell, or the row's created/edited time, in local days. */
export function cellDateRange(row: Row, prop: PropertyDef, tzOffsetMinutes: number): { start: DayTime; end?: DayTime } | undefined {
  if (prop.type === "created_time") return { start: timestampDay(row.createdAt, tzOffsetMinutes) };
  if (prop.type === "edited_time") return { start: timestampDay(row.updatedAt, tzOffsetMinutes) };
  const v = cellValue(row, prop);
  const r = cellResult(row, prop);
  const date = v !== undefined && v.type === "date" ? v.date : r !== undefined && r.kind === "date" ? r.date : undefined;
  if (date === undefined) return undefined;
  const start = parseDate(date.start, tzOffsetMinutes);
  if (start === undefined) return undefined;
  const end = date.end === undefined ? undefined : parseDate(date.end, tzOffsetMinutes);
  return end === undefined ? { start } : { start, end };
}

/** Option ids of select/status options that still exist, in the cell's order. */
function knownOptionIds(prop: PropertyDef, ids: string[]): string[] {
  const known = prop.type === "status" ? (prop.statusOptions ?? []).map((o) => o.id) : (prop.options ?? []).map((o) => o.id);
  return ids.filter((id) => known.includes(id));
}

// ---------- Filters ----------

/** The operators the filter UI offers for a property type (first = default). */
export function filterOperators(type: PropertyType): FilterOperator[] {
  switch (type) {
    case "title":
    case "text":
    case "url":
    case "email":
      return ["contains", "not_contains", "eq", "neq", "starts_with", "ends_with", "is_empty", "is_not_empty"];
    case "number":
      return ["eq", "neq", "gt", "gte", "lt", "lte", "is_empty", "is_not_empty"];
    case "select":
    case "status":
      return ["eq", "neq", "has_any", "has_none", "is_empty", "is_not_empty"];
    case "multi_select":
      return ["has_any", "has_all", "has_none", "is_empty", "is_not_empty"];
    case "person":
      return ["has_any", "has_all", "has_none", "is_me", "is_empty", "is_not_empty"];
    case "created_by":
    case "edited_by":
      return ["has_any", "has_none", "is_me"];
    case "relation":
    case "contact":
      return ["has_any", "has_none", "is_empty", "is_not_empty"];
    case "files":
      return ["is_empty", "is_not_empty"];
    case "checkbox":
      return ["eq"];
    case "date":
      return ["eq", "before", "after", "on_or_before", "on_or_after", "past_week", "next_week", "is_empty", "is_not_empty"];
    case "created_time":
    case "edited_time":
      return ["eq", "before", "after", "on_or_before", "on_or_after", "past_week", "next_week"];
    case "formula":
    case "rollup":
      return ["contains", "not_contains", "eq", "neq", "starts_with", "ends_with", "is_empty", "is_not_empty"];
  }
}

/** The operators the filter UI offers for a property (formulas and rollups: those of their result kind). */
export function propertyFilterOperators(prop: PropertyDef): FilterOperator[] {
  return filterOperators(behavesAs(prop));
}

/** Operators that take no value. */
export function isUnaryOperator(op: FilterOperator): boolean {
  return op === "is_empty" || op === "is_not_empty" || op === "past_week" || op === "next_week" || op === "is_me";
}

/** A new condition for a property (its default operator; checkboxes default to "checked"). */
export function defaultCondition(prop: PropertyDef): FilterCondition {
  const op = propertyFilterOperators(prop)[0]!;
  if (behavesAs(prop) === "checkbox") return { propertyId: prop.id, operator: op, value: true };
  return { propertyId: prop.id, operator: op };
}

function valueList(value: FilterCondition["value"]): string[] | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return value === "" ? [] : [value];
  return undefined;
}

function valueString(value: FilterCondition["value"]): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

function valueNumber(value: FilterCondition["value"]): number | undefined {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function idsCondition(op: FilterOperator, ids: string[], value: FilterCondition["value"], meId: string): boolean {
  switch (op) {
    case "is_empty":
      return ids.length === 0;
    case "is_not_empty":
      return ids.length > 0;
    case "is_me":
      return ids.includes(meId);
    default:
      break;
  }
  const wanted = valueList(value);
  // An incomplete condition (no value picked yet) matches everything, like Notion.
  if (wanted === undefined || wanted.length === 0) return true;
  switch (op) {
    case "eq":
      return ids.length > 0 && ids[0] === wanted[0];
    case "neq":
      return ids.length === 0 || ids[0] !== wanted[0];
    case "has_any":
      return wanted.some((w) => ids.includes(w));
    case "has_all":
      return wanted.every((w) => ids.includes(w));
    case "has_none":
      return !wanted.some((w) => ids.includes(w));
    default:
      return true;
  }
}

function textCondition(op: FilterOperator, text: string, value: FilterCondition["value"]): boolean {
  if (op === "is_empty") return text === "";
  if (op === "is_not_empty") return text !== "";
  const v = valueString(value);
  if (v === undefined || v === "") return true;
  const a = text.toLowerCase();
  const b = v.toLowerCase();
  switch (op) {
    case "eq":
      return a === b;
    case "neq":
      return a !== b;
    case "contains":
      return a.includes(b);
    case "not_contains":
      return !a.includes(b);
    case "starts_with":
      return a.startsWith(b);
    case "ends_with":
      return a.endsWith(b);
    default:
      return true;
  }
}

function numberCondition(op: FilterOperator, n: number | undefined, value: FilterCondition["value"]): boolean {
  if (op === "is_empty") return n === undefined;
  if (op === "is_not_empty") return n !== undefined;
  const v = valueNumber(value);
  if (v === undefined) return true;
  if (op === "neq") return n === undefined || n !== v;
  if (n === undefined) return false;
  switch (op) {
    case "eq":
      return n === v;
    case "gt":
      return n > v;
    case "gte":
      return n >= v;
    case "lt":
      return n < v;
    case "lte":
      return n <= v;
    default:
      return true;
  }
}

function dateCondition(op: FilterOperator, start: DayTime | undefined, value: FilterCondition["value"], ctx: DbContext): boolean {
  if (op === "is_empty") return start === undefined;
  if (op === "is_not_empty") return start !== undefined;
  const now = today(ctx);
  if (op === "past_week") return start !== undefined && start.day >= now - 7 && start.day <= now;
  if (op === "next_week") return start !== undefined && start.day >= now && start.day <= now + 7;
  const s = valueString(value);
  const target = s === undefined ? undefined : parseDate(s, ctx.tzOffsetMinutes);
  if (target === undefined) return true;
  if (start === undefined) return false;
  switch (op) {
    case "eq":
      return start.day === target.day;
    case "before":
      return start.day < target.day;
    case "after":
      return start.day > target.day;
    case "on_or_before":
      return start.day <= target.day;
    case "on_or_after":
      return start.day >= target.day;
    default:
      return true;
  }
}

/** One condition against one row. Unknown properties and operators the type doesn't support match everything. */
export function matchesCondition(db: Database, row: Row, cond: FilterCondition, ctx: DbContext): boolean {
  const prop = propertyById(db, cond.propertyId);
  if (prop === undefined || !propertyFilterOperators(prop).includes(cond.operator)) return true;
  if (isCalculatedType(prop.type)) {
    switch (behavesAs(prop)) {
      case "number":
        return numberCondition(cond.operator, cellNumber(row, prop), cond.value);
      case "checkbox":
        return cellChecked(row, prop) === (cond.value === true || cond.value === "true");
      case "date":
        return dateCondition(cond.operator, cellDateRange(row, prop, ctx.tzOffsetMinutes)?.start, cond.value, ctx);
      default:
        return textCondition(cond.operator, resultText(cellResult(row, prop)), cond.value);
    }
  }
  switch (prop.type) {
    case "title":
    case "text":
    case "url":
    case "email":
      return textCondition(cond.operator, cellText(row, prop), cond.value);
    case "number":
      return numberCondition(cond.operator, cellNumber(row, prop), cond.value);
    case "select":
    case "status":
      return idsCondition(cond.operator, knownOptionIds(prop, cellIds(row, prop)), cond.value, ctx.meId);
    case "multi_select":
      return idsCondition(cond.operator, knownOptionIds(prop, cellIds(row, prop)), cond.value, ctx.meId);
    case "person":
    case "created_by":
    case "edited_by":
    case "relation":
    case "contact":
    case "files":
      return idsCondition(cond.operator, cellIds(row, prop), cond.value, ctx.meId);
    case "checkbox": {
      const want = cond.value === true || cond.value === "true";
      return cellChecked(row, prop) === want;
    }
    case "date":
    case "created_time":
    case "edited_time":
      return dateCondition(cond.operator, cellDateRange(row, prop, ctx.tzOffsetMinutes)?.start, cond.value, ctx);
    case "formula":
    case "rollup":
      return true;
  }
}

/** A view filter against one row ("and": every condition, "or": any; no conditions → true). */
export function matchesFilter(db: Database, row: Row, filter: ViewFilter | undefined, ctx: DbContext): boolean {
  if (filter === undefined || filter.conditions.length === 0) return true;
  if (filter.combinator === "or") return filter.conditions.some((c) => matchesCondition(db, row, c, ctx));
  return filter.conditions.every((c) => matchesCondition(db, row, c, ctx));
}

export function filterRows(db: Database, rows: Row[], filter: ViewFilter | undefined, ctx: DbContext): Row[] {
  if (filter === undefined || filter.conditions.length === 0) return rows;
  return rows.filter((r) => matchesFilter(db, r, filter, ctx));
}

/** Quick search: the query (trimmed, case-insensitive) in the title, text/url/email cells, option or people names. */
export function matchesSearch(db: Database, row: Row, query: string, ctx: DbContext): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  for (const prop of db.properties) {
    switch (prop.type) {
      case "title":
      case "text":
      case "url":
      case "email":
        if (cellText(row, prop).toLowerCase().includes(q)) return true;
        break;
      case "select":
      case "multi_select":
      case "status": {
        const ids = cellIds(row, prop);
        const options: SelectOption[] = prop.type === "status" ? (prop.statusOptions ?? []) : (prop.options ?? []);
        if (options.some((o) => ids.includes(o.id) && o.name.toLowerCase().includes(q))) return true;
        break;
      }
      case "person":
      case "contact":
        if (cellIds(row, prop).some((u) => (ctx.names[u] ?? "").toLowerCase().includes(q))) return true;
        break;
      case "formula":
      case "rollup": {
        const r = cellResult(row, prop);
        if (r !== undefined && r.kind === "text" && r.text.toLowerCase().includes(q)) return true;
        break;
      }
      default:
        break;
    }
  }
  return false;
}

// ---------- Sorting ----------

/** Ordinal comparison by UTF-16 code units (Swift: `a.utf16.lexicographicallyPrecedes(b.utf16)`; Kotlin `compareTo`). */
export function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Manual order: rank, then id (stable for equal ranks). */
export function compareRank(a: Row, b: Row): number {
  const r = compareText(a.rank, b.rank);
  return r !== 0 ? r : compareText(a.id, b.id);
}

export function sortByRank(rows: Row[]): Row[] {
  return rows.toSorted(compareRank);
}

function optionIndex(prop: PropertyDef, optionId: string): number {
  if (prop.type === "status") return statusOptionsInOrder(prop.statusOptions ?? []).findIndex((o) => o.id === optionId);
  return (prop.options ?? []).findIndex((o) => o.id === optionId);
}

/** A comparable key: a number or a string (errors after both); undefined = empty (always sorts last). */
type SortKey = { num?: number; text?: string; error?: string } | undefined;

function sortKey(row: Row, prop: PropertyDef, ctx: DbContext): SortKey {
  switch (prop.type) {
    case "title":
    case "text":
    case "url":
    case "email": {
      const t = cellText(row, prop);
      return t === "" ? undefined : { text: t.toLowerCase() };
    }
    case "number": {
      const n = cellNumber(row, prop);
      return n === undefined ? undefined : { num: n };
    }
    case "select":
    case "status":
    case "multi_select": {
      let best = -1;
      for (const id of cellIds(row, prop)) {
        const i = optionIndex(prop, id);
        if (i >= 0 && (best < 0 || i < best)) best = i;
      }
      return best < 0 ? undefined : { num: best };
    }
    case "person":
    case "contact":
    case "created_by":
    case "edited_by": {
      const names = cellIds(row, prop).map((u) => (ctx.names[u] ?? u).toLowerCase()).toSorted(compareText);
      return names.length === 0 ? undefined : { text: names[0]! };
    }
    case "checkbox":
      return { num: cellChecked(row, prop) ? 1 : 0 };
    case "date":
    case "created_time":
    case "edited_time": {
      const d = cellDateRange(row, prop, ctx.tzOffsetMinutes);
      return d === undefined ? undefined : { num: d.start.day * 1440 + (d.start.minutes ?? -1) };
    }
    case "relation":
    case "files": {
      const n = cellIds(row, prop).length;
      return n === 0 ? undefined : { num: n };
    }
    case "formula":
    case "rollup": {
      const r = cellResult(row, prop);
      if (r === undefined) return undefined;
      switch (r.kind) {
        case "number":
          return { num: r.number };
        case "boolean":
          return { num: r.value ? 1 : 0 };
        case "text":
          return r.text === "" ? undefined : { text: r.text.toLowerCase() };
        case "date": {
          const d = cellDateRange(row, prop, ctx.tzOffsetMinutes);
          return d === undefined ? undefined : { num: d.start.day * 1440 + (d.start.minutes ?? -1) };
        }
        case "error":
          return { error: r.error };
      }
    }
  }
}

function compareKeys(a: SortKey, b: SortKey): number {
  if (a === undefined || b === undefined) return 0;
  if (a.error !== undefined || b.error !== undefined) {
    if (a.error !== undefined && b.error !== undefined) return compareText(a.error, b.error);
    return a.error !== undefined ? 1 : -1;
  }
  if (a.num !== undefined && b.num !== undefined) return a.num === b.num ? 0 : a.num < b.num ? -1 : 1;
  return compareText(a.text ?? "", b.text ?? "");
}

/**
 * Multi-key sort, stable: ties keep manual (rank) order. Empty values go last for both directions.
 * Unknown properties are ignored. No sorts → rank order.
 */
export function sortRows(db: Database, rows: Row[], sorts: ViewSort[], ctx: DbContext): Row[] {
  const base = sortByRank(rows);
  const keys: { prop: PropertyDef; dir: number }[] = [];
  for (const s of sorts) {
    const prop = propertyById(db, s.propertyId);
    if (prop !== undefined) keys.push({ prop, dir: s.direction === "desc" ? -1 : 1 });
  }
  if (keys.length === 0) return base;
  const decorated = base.map((row, index) => ({ row, index, keys: keys.map((k) => sortKey(row, k.prop, ctx)) }));
  decorated.sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const ka = a.keys[i];
      const kb = b.keys[i];
      if (ka === undefined && kb === undefined) continue;
      if (ka === undefined) return 1;
      if (kb === undefined) return -1;
      const c = compareKeys(ka, kb);
      // Errors stay after values in both directions (like empty cells, which go last).
      if ((ka.error === undefined) !== (kb.error === undefined)) return c;
      if (c !== 0) return c * keys[i]!.dir;
    }
    return a.index - b.index;
  });
  return decorated.map((d) => d.row);
}

// ---------- Grouping ----------

export type RowGroup = {
  /** Option id, user id, "true"/"false" (checkbox) or "none". */
  key: string;
  /** Option or member name ("" for the "none" and checkbox groups, which use `label`). */
  name: string;
  /** For groups without a name: "No Status", "Checked", "Unchecked". */
  label?: MessageDescriptor;
  color?: OptionColor;
  statusGroup?: StatusGroup;
  userId?: string;
  /** In `view.hiddenGroups`: listed under "Hidden groups", not shown as a column. */
  hidden: boolean;
  /** In the order given (already sorted). */
  rows: Row[];
};

/** The group keys a row belongs to (a row with several people appears under each). */
export function rowGroupKeys(prop: PropertyDef, row: Row): string[] {
  if (prop.type === "checkbox") return [cellChecked(row, prop) ? "true" : "false"];
  if (isCalculatedType(prop.type)) return [resultGroupKey(prop, row)];
  const ids = prop.type === "select" || prop.type === "status" ? knownOptionIds(prop, cellIds(row, prop)) : cellIds(row, prop);
  return ids.length === 0 ? [NONE_GROUP] : ids;
}

/**
 * The group of a formula or rollup cell: checkbox results "true"/"false"; dates their day "YYYY-MM-DD"; text its value
 * as is; errors their code; empty "none".
 */
function resultGroupKey(prop: PropertyDef, row: Row): string {
  const r = cellResult(row, prop);
  if (behavesAs(prop) === "checkbox") return r !== undefined && r.kind === "boolean" && r.value ? "true" : "false";
  if (r === undefined) return NONE_GROUP;
  if (r.kind === "date") return r.date.start.slice(0, 10);
  const t = resultText(r);
  return t === "" ? NONE_GROUP : t;
}

/**
 * Groups rows (in their given order) by the view's select, status, person or checkbox property, or a formula/rollup
 * by its result (checked/unchecked; other values ascending, empty first). Order: "No <prop>"
 * first (status: only when it has rows), then options in order (status: To-do → In progress → Complete), people by
 * name, or Unchecked → Checked. Not grouped (no or unknown property) → [].
 */
export function groupRows(db: Database, rows: Row[], view: DbView, ctx: DbContext): RowGroup[] {
  const prop = view.groupByPropertyId === undefined ? undefined : propertyById(db, view.groupByPropertyId);
  if (prop === undefined || !isGroupableProperty(prop)) return [];
  const hidden = view.hiddenGroups ?? [];
  const groups: RowGroup[] = [];
  const add = (g: Omit<RowGroup, "hidden" | "rows">) => groups.push({ ...g, hidden: hidden.includes(g.key), rows: [] });
  const none = { key: NONE_GROUP, name: "", label: msg("db.group.none", { property: prop.name }) };
  if (prop.type === "select") {
    add(none);
    for (const o of prop.options ?? []) add({ key: o.id, name: o.name, color: o.color });
  } else if (prop.type === "status") {
    add(none);
    for (const o of statusOptionsInOrder(prop.statusOptions ?? [])) add({ key: o.id, name: o.name, color: o.color, statusGroup: o.group });
  } else if (prop.type === "person") {
    add(none);
    const users: string[] = [];
    for (const r of rows) for (const u of cellIds(r, prop)) if (!users.includes(u)) users.push(u);
    const named = users.map((u) => ({ u, name: ctx.names[u] ?? u })).toSorted((a, b) => compareText(a.name.toLowerCase(), b.name.toLowerCase()) || compareText(a.u, b.u));
    for (const x of named) add({ key: x.u, name: x.name, userId: x.u });
  } else if (isCalculatedType(prop.type) && behavesAs(prop) !== "checkbox") {
    add(none);
    const keys: string[] = [];
    for (const r of rows) {
      const k = resultGroupKey(prop, r);
      if (k !== NONE_GROUP && !keys.includes(k)) keys.push(k);
    }
    for (const k of keys.toSorted((a, b) => compareText(a.toLowerCase(), b.toLowerCase()) || compareText(a, b))) add({ key: k, name: k });
  } else {
    add({ key: "false", name: "", label: msg("db.group.unchecked") });
    add({ key: "true", name: "", label: msg("db.group.checked") });
  }
  for (const r of rows) {
    for (const key of rowGroupKeys(prop, r)) groups.find((g) => g.key === key)?.rows.push(r);
  }
  return prop.type === "status" ? groups.filter((g) => g.key !== NONE_GROUP || g.rows.length > 0) : groups;
}

/** Shows or hides a group in a view. */
export function setGroupHidden(hiddenGroups: string[] | undefined, key: string, hidden: boolean): string[] {
  const without = (hiddenGroups ?? []).filter((k) => k !== key);
  return hidden ? [...without, key] : without;
}

/** The cell that puts a row into a group (null clears it), or undefined when the property can't be grouped. */
export function groupCellValue(prop: PropertyDef, key: string, current: Row | undefined, fromKey: string | undefined): PropertyValue | null | undefined {
  switch (prop.type) {
    case "select":
      return key === NONE_GROUP ? null : { type: "select", optionId: key };
    case "status":
      return key === NONE_GROUP ? null : { type: "status", optionId: key };
    case "checkbox":
      return { type: "checkbox", checked: key === "true" };
    case "person": {
      if (key === NONE_GROUP) return null;
      const now = current === undefined ? [] : cellIds(current, prop);
      const kept = now.filter((u) => u !== fromKey && u !== key);
      return { type: "person", userIds: [...kept, key] };
    }
    default:
      return undefined;
  }
}

// ---------- Ranks (fractional-indexing) ----------

/** A rank strictly between two ranks (either end open). Out-of-order bounds (bad data) → after `before`. */
export function rankBetween(before: string | undefined, after: string | undefined): string {
  if (before !== undefined && after !== undefined && compareText(before, after) >= 0) return generateKeyBetween(before, null);
  return generateKeyBetween(before ?? null, after ?? null);
}

/** A rank after every row. */
export function rankAfterLast(rows: Row[]): string {
  let max: string | undefined = undefined;
  for (const r of rows) if (max === undefined || compareText(r.rank, max) > 0) max = r.rank;
  return rankBetween(max, undefined);
}

/** A drag result: the cells to write and, for manual order, the new rank and neighbours (UpdateRowRequest). */
export type CardMove = {
  rowId: string;
  /** propertyId → new value (null clears); empty when the card stays in its group. */
  values: Record<string, PropertyValue | null>;
  rank?: string;
  afterId?: string;
  beforeId?: string;
};

/**
 * Moves a card from group `fromKey` to `toIndex` (its position after the move) in group `toKey`. Changes the
 * grouped cell when the group changes; with manual order (no sorts) also computes the rank between the new
 * neighbours. Returns undefined for a no-op (same place) or unknown row/group.
 */
export function moveCard(db: Database, view: DbView, groups: RowGroup[], rowId: string, fromKey: string, toKey: string, toIndex: number): CardMove | undefined {
  const prop = view.groupByPropertyId === undefined ? undefined : propertyById(db, view.groupByPropertyId);
  const from = groups.find((g) => g.key === fromKey);
  const to = groups.find((g) => g.key === toKey);
  if (prop === undefined || from === undefined || to === undefined) return undefined;
  const row = from.rows.find((r) => r.id === rowId);
  if (row === undefined) return undefined;
  const values: Record<string, PropertyValue | null> = {};
  if (fromKey !== toKey) {
    const v = groupCellValue(prop, toKey, row, fromKey);
    if (v === undefined) return undefined;
    values[prop.id] = v;
  }
  const move: CardMove = { rowId, values };
  if (view.sorts.length === 0) {
    const others = to.rows.filter((r) => r.id !== rowId);
    const at = Math.max(0, Math.min(toIndex, others.length));
    const prev = at > 0 ? others[at - 1] : undefined;
    const next = others[at];
    const sameSpot = fromKey === toKey && from.rows.indexOf(row) === at;
    // Into an empty column the manual order doesn't change.
    if (!sameSpot && (prev !== undefined || next !== undefined)) {
      move.rank = rankBetween(prev?.rank, next?.rank);
      if (prev !== undefined) move.afterId = prev.id;
      if (next !== undefined) move.beforeId = next.id;
    }
  }
  if (Object.keys(values).length === 0 && move.rank === undefined) return undefined;
  return move;
}

/**
 * Cells for a row created from a view: the group it was added to, the calendar day it was added on, and simple
 * equality filters (select/status is, checkbox, person is me) so the new row stays visible.
 */
export function valuesForNewRow(db: Database, view: DbView, groupKey: string | undefined, day: string | undefined, ctx: DbContext): Record<string, PropertyValue> {
  const values: Record<string, PropertyValue> = {};
  for (const c of view.filter?.combinator === "and" ? view.filter.conditions : []) {
    const prop = propertyById(db, c.propertyId);
    if (prop === undefined) continue;
    if ((prop.type === "select" || prop.type === "status") && (c.operator === "eq" || c.operator === "has_any")) {
      const id = valueList(c.value)?.[0];
      if (id !== undefined) values[prop.id] = prop.type === "select" ? { type: "select", optionId: id } : { type: "status", optionId: id };
    } else if (prop.type === "checkbox" && c.operator === "eq") {
      values[prop.id] = { type: "checkbox", checked: c.value === true || c.value === "true" };
    } else if (prop.type === "person" && c.operator === "is_me") {
      values[prop.id] = { type: "person", userIds: [ctx.meId] };
    }
  }
  const groupProp = view.groupByPropertyId === undefined ? undefined : propertyById(db, view.groupByPropertyId);
  if (groupKey !== undefined && groupProp !== undefined) {
    const v = groupCellValue(groupProp, groupKey, undefined, undefined);
    if (v === null) delete values[groupProp.id];
    else if (v !== undefined) values[groupProp.id] = v;
  }
  const dateProp = view.datePropertyId === undefined ? undefined : propertyById(db, view.datePropertyId);
  if (day !== undefined && dateProp !== undefined && dateProp.type === "date") values[dateProp.id] = { type: "date", date: { start: day } };
  return values;
}

// ---------- Calculations (the Calculate row, rollups) ----------

const COUNT_CALCULATIONS: Calculation[] = ["count_all", "count_values", "count_unique", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty"];

/** The calculations a property offers, in menu order (Notion): checkboxes count checked; numbers and dates add theirs. */
export function calculationsFor(prop: PropertyDef): Calculation[] {
  const t = behavesAs(prop);
  if (t === "checkbox") return ["count_all", "checked", "unchecked", "percent_checked", "percent_unchecked"];
  if (t === "number") return [...COUNT_CALCULATIONS, "sum", "average", "median", "min", "max", "range"];
  if (t === "date" || t === "created_time" || t === "edited_time") return [...COUNT_CALCULATIONS, "earliest", "latest", "date_range"];
  return COUNT_CALCULATIONS;
}

/** A calculation's result. Percents are fractions (0.25 = 25%); `none` = nothing to show (no numbers, no dates). */
export type CalcValue =
  | { kind: "count"; count: number }
  | { kind: "percent"; fraction: number }
  | { kind: "number"; number: number; format: "number" | "percent" | "currency" }
  | { kind: "date"; date: DateParts }
  | { kind: "days"; days: number }
  | { kind: "none" };

/** The values a cell counts as (Count values counts each tag/person; empty cells have none; errors count). */
function countedValues(row: Row, prop: PropertyDef, tzOffsetMinutes: number): string[] {
  switch (prop.type) {
    case "title":
    case "text":
    case "url":
    case "email": {
      const t = cellText(row, prop);
      return t === "" ? [] : [t];
    }
    case "number": {
      const n = cellNumber(row, prop);
      return n === undefined ? [] : [String(n)];
    }
    case "checkbox":
      return cellChecked(row, prop) ? ["true"] : [];
    case "date":
    case "created_time":
    case "edited_time": {
      const d = cellDateRange(row, prop, tzOffsetMinutes);
      return d === undefined ? [] : [`${d.start.day}:${d.start.minutes ?? ""}`];
    }
    case "select":
    case "status":
      return knownOptionIds(prop, cellIds(row, prop));
    case "multi_select":
      return knownOptionIds(prop, cellIds(row, prop));
    case "formula":
    case "rollup": {
      const r = cellResult(row, prop);
      if (r === undefined) return [];
      const t = r.kind === "error" ? r.error : resultText(r);
      return t === "" ? [] : [t];
    }
    default:
      return cellIds(row, prop);
  }
}

/** The number format a property's numbers use (number columns, number formulas and rollups). */
export function numberFormatOf(prop: PropertyDef): "number" | "percent" | "currency" {
  if (prop.type === "number") return prop.numberFormat ?? "number";
  return isCalculatedType(prop.type) ? calculatedNumberFormat(prop) : "number";
}

function dayTimeKey(d: DayTime): number {
  return d.day * 1440 + (d.minutes ?? 0);
}

/**
 * One calculation over rows (the view's rows for the Calculate row, the related rows for a rollup). Empty cells and
 * errors are skipped by number and date calculations; a calculation the property doesn't offer gives `none`.
 */
export function calculate(prop: PropertyDef, rows: Row[], calc: Calculation, ctx: DbContext): CalcValue {
  if (!calculationsFor(prop).includes(calc)) return { kind: "none" };
  const total = rows.length;
  const fraction = (n: number): CalcValue => ({ kind: "percent", fraction: total === 0 ? 0 : n / total });
  switch (calc) {
    case "count_all":
      return { kind: "count", count: total };
    case "count_values":
      return { kind: "count", count: rows.reduce((n, r) => n + countedValues(r, prop, ctx.tzOffsetMinutes).length, 0) };
    case "count_unique": {
      const seen = new Set<string>();
      for (const r of rows) for (const v of countedValues(r, prop, ctx.tzOffsetMinutes)) seen.add(v);
      return { kind: "count", count: seen.size };
    }
    case "count_empty":
    case "count_not_empty":
    case "percent_empty":
    case "percent_not_empty": {
      const empty = rows.filter((r) => countedValues(r, prop, ctx.tzOffsetMinutes).length === 0).length;
      if (calc === "count_empty") return { kind: "count", count: empty };
      if (calc === "count_not_empty") return { kind: "count", count: total - empty };
      return fraction(calc === "percent_empty" ? empty : total - empty);
    }
    case "checked":
    case "unchecked":
    case "percent_checked":
    case "percent_unchecked": {
      const checked = rows.filter((r) => cellChecked(r, prop)).length;
      if (calc === "checked") return { kind: "count", count: checked };
      if (calc === "unchecked") return { kind: "count", count: total - checked };
      return fraction(calc === "percent_checked" ? checked : total - checked);
    }
    case "sum":
    case "average":
    case "median":
    case "min":
    case "max":
    case "range": {
      const nums: number[] = [];
      for (const r of rows) {
        const n = cellNumber(r, prop);
        if (n !== undefined) nums.push(n);
      }
      if (nums.length === 0) return calc === "sum" ? { kind: "number", number: 0, format: numberFormatOf(prop) } : { kind: "none" };
      const sorted = nums.toSorted((a, b) => a - b);
      const sum = nums.reduce((a, b) => a + b, 0);
      const mid = Math.floor(sorted.length / 2);
      const value =
        calc === "sum" ? sum
        : calc === "average" ? sum / nums.length
        : calc === "median" ? (sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2)
        : calc === "min" ? sorted[0]!
        : calc === "max" ? sorted[sorted.length - 1]!
        : sorted[sorted.length - 1]! - sorted[0]!;
      return { kind: "number", number: value, format: numberFormatOf(prop) };
    }
    case "earliest":
    case "latest":
    case "date_range": {
      let first: DayTime | undefined;
      let last: DayTime | undefined;
      for (const r of rows) {
        const d = cellDateRange(r, prop, ctx.tzOffsetMinutes);
        if (d === undefined) continue;
        const end = d.end ?? d.start;
        if (first === undefined || dayTimeKey(d.start) < dayTimeKey(first)) first = d.start;
        if (last === undefined || dayTimeKey(end) > dayTimeKey(last)) last = end;
      }
      if (first === undefined || last === undefined) return { kind: "none" };
      if (calc === "earliest") return { kind: "date", date: dateParts(first) };
      if (calc === "latest") return { kind: "date", date: dateParts(last) };
      return { kind: "days", days: last.day - first.day };
    }
  }
}

/** The short label of a calculation (footer and menu). */
export function calculationLabel(calc: Calculation): MessageDescriptor {
  return msg(`db.calc.${calc.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase())}` as MessageKey);
}

/** The Calculate row of a view: property id → result, for the visible properties that have a calculation. */
export function viewCalculations(db: Database, view: DbView, rows: Row[], ctx: DbContext): Record<string, CalcValue> {
  const out: Record<string, CalcValue> = {};
  for (const [id, calc] of Object.entries(view.calculations ?? {})) {
    const prop = propertyById(db, id);
    if (prop !== undefined) out[id] = calculate(prop, rows, calc, ctx);
  }
  return out;
}

/**
 * A rollup's result over the related rows (`targetProp` of the related database). Counts and numbers → number
 * (percents as fractions), earliest/latest → date (YYYY-MM-DD), date range → days, show_original → the related
 * cells' text joined with ", " (`labels`: each related row's cell as text, options, people and pages named). Undefined =
 * empty.
 */
export function rollupResult(rollup: RollupDef, targetProp: PropertyDef, related: Row[], ctx: DbContext, labels: string[]): FormulaResult | undefined {
  if (rollup.calculate === "show_original") {
    const parts = labels.filter((t) => t !== "");
    return parts.length === 0 ? undefined : { kind: "text", text: parts.join(", ") };
  }
  const v = calculate(targetProp, related, rollup.calculate, ctx);
  switch (v.kind) {
    case "count":
      return { kind: "number", number: v.count };
    case "percent":
      return { kind: "number", number: v.fraction };
    case "number":
      return { kind: "number", number: v.number };
    case "days":
      return { kind: "number", number: v.days };
    case "date":
      return { kind: "date", date: { start: formatDay(daysFromCivil(v.date.year, v.date.month, v.date.day)) } };
    case "none":
      return undefined;
  }
}

// ---------- View pipeline ----------

export type ViewData = {
  /** Filtered, searched and sorted rows. */
  rows: Row[];
  /** `rows` grouped (empty when the view isn't grouped). */
  groups: RowGroup[];
};

/** filter → search → sort → group. */
export function computeView(db: Database, rows: Row[], view: DbView, search: string, ctx: DbContext): ViewData {
  const visible = rows.filter((r) => r.databaseId === db.id && matchesFilter(db, r, view.filter, ctx) && matchesSearch(db, r, search, ctx));
  const sorted = sortRows(db, visible, view.sorts, ctx);
  return { rows: sorted, groups: groupRows(db, sorted, view, ctx) };
}

/** A new view of `layout` with sensible defaults (board: first groupable property; calendar: first date). */
export function newViewRequest(db: Database, layout: ViewLayout, name: string): CreateViewRequest {
  const req: CreateViewRequest = { name, layout, sorts: [], visiblePropertyIds: [] };
  const patch = layoutPatch(db, { visiblePropertyIds: [] }, layout);
  return { ...req, ...patch };
}

/**
 * The view fields to change when switching layout: board needs a group (status, else select, person, checkbox),
 * calendar needs a date property. Visible properties default to the first few when the view shows none.
 */
export function layoutPatch(db: Database, view: { groupByPropertyId?: string; datePropertyId?: string; visiblePropertyIds: string[] }, layout: ViewLayout): Partial<CreateViewRequest> {
  const patch: Partial<CreateViewRequest> = { layout };
  if (layout === "board" && view.groupByPropertyId === undefined) {
    const prop = db.properties.find((p) => p.type === "status") ?? db.properties.find((p) => p.type === "select") ?? db.properties.find((p) => isGroupable(p.type));
    if (prop !== undefined) patch.groupByPropertyId = prop.id;
  }
  if (layout === "calendar" && view.datePropertyId === undefined) {
    const prop = dateProperties(db)[0];
    if (prop !== undefined) patch.datePropertyId = prop.id;
  }
  if (view.visiblePropertyIds.length === 0) {
    const count = layout === "table" ? 6 : 3;
    patch.visiblePropertyIds = db.properties.filter((p) => p.type !== "title").slice(0, count).map((p) => p.id);
  }
  return patch;
}

// ---------- Calendar ----------

/** First day of the week for a locale (0 = Sunday … 6 = Saturday): Sunday for en/en-US and pt-BR, Monday elsewhere. */
export function weekStartFor(locale: string): number {
  const l = locale.toLowerCase();
  if (l === "en" || l === "en-us" || l === "pt-br" || l === "pt" || l === "en-ca" || l === "ja" || l === "ja-jp") return 0;
  return 1;
}

export type CalendarDay = {
  /** "YYYY-MM-DD". */
  date: string;
  year: number;
  month: number;
  /** Day of month. */
  day: number;
  /** 0 = Sunday. */
  weekday: number;
  inMonth: boolean;
  isToday: boolean;
  /** Rows on this day that didn't fit in `maxLanes`. */
  more: number;
};

/** One row's piece in one week. */
export type CalendarSlot = {
  rowId: string;
  /** The piece starts in this column (show the title here). */
  starts: boolean;
  /** The row's range started in an earlier week / ends in a later week. */
  continuesBefore: boolean;
  continuesAfter: boolean;
};

export type CalendarWeek = {
  days: CalendarDay[];
  /** Lanes top → bottom, each with 7 cells: the slot covering that day, or null. */
  lanes: (CalendarSlot | null)[][];
};

export type MonthGrid = { year: number; month: number; weeks: CalendarWeek[] };

/** Year/month `delta` months away. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  const y = Math.floor(index / 12);
  return { year: y, month: index - y * 12 + 1 };
}

/** The month containing a day number. */
export function monthOfDay(day: number): { year: number; month: number } {
  const c = civilFromDays(day);
  return { year: c.year, month: c.month };
}

/**
 * Month grid with rows placed by `datePropertyId` (multi-day ranges span columns and keep one lane per week).
 * Weeks start on `weekStartsOn` and cover the whole month. Within a week, earlier starts go first, then longer
 * ranges, then earlier times, then the given row order. Rows beyond `maxLanes` count in `more` per day.
 */
export function monthGrid(db: Database, rows: Row[], datePropertyId: string, year: number, month: number, weekStartsOn: number, maxLanes: number, ctx: DbContext): MonthGrid {
  const prop = propertyById(db, datePropertyId);
  const first = daysFromCivil(year, month, 1);
  const next = shiftMonth(year, month, 1);
  const last = daysFromCivil(next.year, next.month, 1) - 1;
  const firstWeekday = civilFromDays(first).weekday;
  const gridStart = first - ((firstWeekday - weekStartsOn + 7) % 7);
  const lastWeekday = civilFromDays(last).weekday;
  const gridEnd = last + ((weekStartsOn + 6 - lastWeekday + 7) % 7);
  const now = today(ctx);

  type Item = { rowId: string; order: number; start: number; end: number; minutes: number };
  const items: Item[] = [];
  if (prop !== undefined) {
    rows.forEach((r, order) => {
      const range = cellDateRange(r, prop, ctx.tzOffsetMinutes);
      if (range === undefined) return;
      const start = range.start.day;
      const end = range.end === undefined || range.end.day < start ? start : range.end.day;
      if (end < gridStart || start > gridEnd) return;
      items.push({ rowId: r.id, order, start, end, minutes: range.start.minutes ?? -1 });
    });
  }

  const weeks: CalendarWeek[] = [];
  for (let ws = gridStart; ws <= gridEnd; ws += 7) {
    const we = ws + 6;
    const days: CalendarDay[] = [];
    for (let d = ws; d <= we; d++) {
      const c = civilFromDays(d);
      days.push({ date: formatDay(d), year: c.year, month: c.month, day: c.day, weekday: c.weekday, inMonth: d >= first && d <= last, isToday: d === now, more: 0 });
    }
    const inWeek = items
      .filter((it) => it.end >= ws && it.start <= we)
      .toSorted((a, b) => {
        const sa = Math.max(a.start, ws);
        const sb = Math.max(b.start, ws);
        if (sa !== sb) return sa - sb;
        const la = Math.min(a.end, we) - sa;
        const lb = Math.min(b.end, we) - sb;
        if (la !== lb) return lb - la;
        if (a.minutes !== b.minutes) return a.minutes - b.minutes;
        return a.order - b.order;
      });
    const lanes: (CalendarSlot | null)[][] = [];
    for (const it of inWeek) {
      const from = Math.max(it.start, ws) - ws;
      const to = Math.min(it.end, we) - ws;
      let lane = lanes.findIndex((l) => {
        for (let col = from; col <= to; col++) if (l[col] !== null) return false;
        return true;
      });
      if (lane < 0) {
        if (lanes.length >= maxLanes) {
          for (let col = from; col <= to; col++) days[col]!.more += 1;
          continue;
        }
        lanes.push([null, null, null, null, null, null, null]);
        lane = lanes.length - 1;
      }
      for (let col = from; col <= to; col++) {
        lanes[lane]![col] = { rowId: it.rowId, starts: col === from, continuesBefore: it.start < ws, continuesAfter: it.end > we };
      }
    }
    weeks.push({ days, lanes });
  }
  return { year, month, weeks };
}

// ---------- Display ----------

export type DateParts = { year: number; month: number; day: number; hour?: number; minute?: number };

/** How to show a cell; the UI formats numbers, dates and names with the platform formatters. */
export type CellDisplay =
  | { kind: "empty" }
  | { kind: "text"; text: string; link?: "url" | "email" }
  | { kind: "number"; number: number; format: "number" | "percent" | "currency" }
  | { kind: "options"; options: SelectOption[] }
  | { kind: "status"; option: StatusOption }
  | { kind: "date"; start: DateParts; end?: DateParts }
  | { kind: "people"; userIds: string[] }
  | { kind: "checkbox"; checked: boolean }
  | { kind: "files"; count: number }
  | { kind: "relation"; pageIds: string[] }
  /** Mail contacts (names from the contacts list, like `people` from members). */
  | { kind: "contacts"; contactIds: string[] }
  | { kind: "timestamp"; at: number }
  | { kind: "error"; error: FormulaErrorCode; message?: string };

/** Civil date (and time) parts of a local day/time, for display. */
export function dateParts(d: DayTime): DateParts {
  const c = civilFromDays(d.day);
  const p: DateParts = { year: c.year, month: c.month, day: c.day };
  if (d.minutes !== undefined) {
    p.hour = Math.floor(d.minutes / 60);
    p.minute = d.minutes % 60;
  }
  return p;
}

/** Display descriptor of one cell (unknown option ids are dropped; an all-unknown select reads as empty). */
export function displayValue(prop: PropertyDef, row: Row, tzOffsetMinutes: number): CellDisplay {
  switch (prop.type) {
    case "created_time":
      return { kind: "timestamp", at: row.createdAt };
    case "edited_time":
      return { kind: "timestamp", at: row.updatedAt };
    case "created_by":
      return { kind: "people", userIds: [row.createdBy] };
    case "edited_by":
      return { kind: "people", userIds: [row.updatedBy] };
    default:
      break;
  }
  const raw = row.values[prop.id];
  if (raw !== undefined && raw.type === "cell_formula" && raw.result.kind === "error") return resultDisplay(prop, row, raw.result, tzOffsetMinutes);
  const v = cellValue(row, prop);
  if (v === undefined) return prop.type === "checkbox" ? { kind: "checkbox", checked: false } : { kind: "empty" };
  switch (v.type) {
    case "title":
    case "text":
      return v.text === "" ? { kind: "empty" } : { kind: "text", text: v.text };
    case "url":
      return v.url === "" ? { kind: "empty" } : { kind: "text", text: v.url, link: "url" };
    case "email":
      return v.email === "" ? { kind: "empty" } : { kind: "text", text: v.email, link: "email" };
    case "number":
      return { kind: "number", number: v.number, format: prop.numberFormat ?? "number" };
    case "select":
    case "multi_select": {
      const ids = v.type === "select" ? [v.optionId] : v.optionIds;
      const options: SelectOption[] = [];
      for (const id of ids) {
        const o = (prop.options ?? []).find((x) => x.id === id);
        if (o !== undefined) options.push(o);
      }
      return options.length === 0 ? { kind: "empty" } : { kind: "options", options };
    }
    case "status": {
      const o = (prop.statusOptions ?? []).find((x) => x.id === v.optionId);
      return o === undefined ? { kind: "empty" } : { kind: "status", option: o };
    }
    case "date": {
      const range = cellDateRange(row, prop, tzOffsetMinutes);
      if (range === undefined) return { kind: "empty" };
      const d: CellDisplay = { kind: "date", start: dateParts(range.start) };
      if (range.end !== undefined) d.end = dateParts(range.end);
      return d;
    }
    case "person":
      return v.userIds.length === 0 ? { kind: "empty" } : { kind: "people", userIds: v.userIds };
    case "checkbox":
      return { kind: "checkbox", checked: v.checked };
    case "files":
      return v.fileIds.length === 0 ? { kind: "empty" } : { kind: "files", count: v.fileIds.length };
    case "relation":
      return v.pageIds.length === 0 ? { kind: "empty" } : { kind: "relation", pageIds: v.pageIds };
    case "contact":
      return v.contactIds.length === 0 ? { kind: "empty" } : { kind: "contacts", contactIds: v.contactIds };
    case "formula":
    case "rollup":
      return resultDisplay(prop, row, v.result, tzOffsetMinutes);
    case "cell_formula": // cellValue reads cell formulas as typed values
      return { kind: "empty" };
  }
}

/** Number format of a formula or rollup's numbers (percent rollups show percents; a date format shows plain numbers). */
export function calculatedNumberFormat(prop: PropertyDef): "number" | "percent" | "currency" {
  if (prop.type === "rollup") {
    const c = prop.rollup?.calculate;
    return c === "percent_empty" || c === "percent_not_empty" || c === "percent_checked" || c === "percent_unchecked" ? "percent" : "number";
  }
  const f = prop.formula?.format;
  return f === "percent" || f === "currency" ? f : "number";
}

function resultDisplay(prop: PropertyDef, row: Row, r: FormulaResult, tzOffsetMinutes: number): CellDisplay {
  switch (r.kind) {
    case "number":
      return { kind: "number", number: r.number, format: calculatedNumberFormat(prop) };
    case "text":
      return r.text === "" ? { kind: "empty" } : { kind: "text", text: r.text };
    case "boolean":
      return { kind: "checkbox", checked: r.value };
    case "date": {
      const range = cellDateRange(row, prop, tzOffsetMinutes);
      if (range === undefined) return { kind: "empty" };
      const d: CellDisplay = { kind: "date", start: dateParts(range.start) };
      if (range.end !== undefined) d.end = dateParts(range.end);
      return d;
    }
    case "error":
      return r.message === undefined ? { kind: "error", error: r.error } : { kind: "error", error: r.error, message: r.message };
  }
}

/** True when a status cell is in the "complete" group (done styling). */
export function isComplete(db: Database, row: Row): boolean {
  for (const prop of db.properties) {
    if (prop.type !== "status") continue;
    const id = cellIds(row, prop)[0];
    const o = (prop.statusOptions ?? []).find((x) => x.id === id);
    if (o !== undefined) return o.group === "complete";
  }
  return false;
}

// ---------- Cache reducer ----------

/** Inserts or replaces a row, keeping rank order. */
export function upsertRow(rows: Row[], row: Row): Row[] {
  return sortByRank([...rows.filter((r) => r.id !== row.id), row]);
}

export function removeRow(rows: Row[], rowId: string): Row[] {
  return rows.some((r) => r.id === rowId) ? rows.filter((r) => r.id !== rowId) : rows;
}

/** Optimistic cell edit: listed cells replaced, null clears; rank/icon when given; edited by/at updated. */
export function patchRow(row: Row, values: Record<string, PropertyValue | null>, rank: string | undefined, icon: string | undefined, at: number, by: string): Row {
  const next: Record<string, PropertyValue> = { ...row.values };
  for (const [k, v] of Object.entries(values)) {
    if (v === null) delete next[k];
    else next[k] = v;
  }
  const out: Row = { ...row, values: next, updatedAt: at, updatedBy: by };
  if (rank !== undefined) out.rank = rank;
  if (icon !== undefined) out.icon = icon;
  return out;
}

function compareViews(a: DbView, b: DbView): number {
  const r = compareText(a.rank, b.rank);
  return r !== 0 ? r : compareText(a.id, b.id);
}

/**
 * A view after a partial update (optimistic, like the server): absent fields are kept, `null` clears group-by,
 * date property and filter; `afterId` (placement) is the server's job and ignored here.
 */
export function patchView(view: DbView, req: UpdateViewRequest): DbView {
  const out: DbView = { ...view };
  if (req.name !== undefined) out.name = req.name;
  if (req.layout !== undefined) out.layout = req.layout;
  if (req.sorts !== undefined) out.sorts = req.sorts;
  if (req.visiblePropertyIds !== undefined) out.visiblePropertyIds = req.visiblePropertyIds;
  if (req.hiddenGroups !== undefined) out.hiddenGroups = req.hiddenGroups;
  if (req.columnWidths !== undefined) out.columnWidths = req.columnWidths;
  if (req.calculations !== undefined) out.calculations = req.calculations;
  if (req.showRowNumbers !== undefined) out.showRowNumbers = req.showRowNumbers;
  if (req.sheet !== undefined) out.sheet = req.sheet;
  if (req.groupByPropertyId === null) delete out.groupByPropertyId;
  else if (req.groupByPropertyId !== undefined) out.groupByPropertyId = req.groupByPropertyId;
  if (req.datePropertyId === null) delete out.datePropertyId;
  else if (req.datePropertyId !== undefined) out.datePropertyId = req.datePropertyId;
  if (req.filter === null) delete out.filter;
  else if (req.filter !== undefined) out.filter = req.filter;
  return out;
}

/** Applies an UpdateDatabaseRequest locally (optimistic): title, icon, properties, the default row template. */
export function patchDatabase(db: Database, req: UpdateDatabaseRequest): Database {
  const out: Database = { ...db };
  if (req.title !== undefined) out.title = req.title;
  if (req.icon !== undefined) {
    if (req.icon === "") delete out.icon;
    else out.icon = req.icon;
  }
  if (req.properties !== undefined) out.properties = req.properties;
  if (req.defaultRowTemplateId === null) delete out.defaultRowTemplateId;
  else if (req.defaultRowTemplateId !== undefined) out.defaultRowTemplateId = req.defaultRowTemplateId;
  return out;
}

/** Inserts or replaces a view, keeping rank order. */
export function upsertView(views: DbView[], view: DbView): DbView[] {
  return [...views.filter((v) => v.id !== view.id), view].toSorted(compareViews);
}

export function removeView(views: DbView[], viewId: string): DbView[] {
  return views.some((v) => v.id === viewId) ? views.filter((v) => v.id !== viewId) : views;
}

/** The optimistic row of a create (temporary id = clientId). */
export function optimisticRow(databaseId: string, input: NewRowInput, meId: string): Row {
  return { id: input.clientId, databaseId, rank: input.rank, values: input.values, createdBy: meId, createdAt: input.at, updatedBy: meId, updatedAt: input.at };
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Deep equality of two cells. */
export function sameValue(a: PropertyValue, b: PropertyValue): boolean {
  switch (a.type) {
    case "title":
    case "text":
      return (b.type === "title" || b.type === "text") && a.type === b.type && a.text === b.text;
    case "number":
      return b.type === "number" && a.number === b.number;
    case "select":
    case "status":
      return (b.type === "select" || b.type === "status") && a.type === b.type && a.optionId === b.optionId;
    case "multi_select":
      return b.type === "multi_select" && sameIds(a.optionIds, b.optionIds);
    case "date":
      return b.type === "date" && a.date.start === b.date.start && (a.date.end ?? "") === (b.date.end ?? "");
    case "person":
      return b.type === "person" && sameIds(a.userIds, b.userIds);
    case "checkbox":
      return b.type === "checkbox" && a.checked === b.checked;
    case "url":
      return b.type === "url" && a.url === b.url;
    case "email":
      return b.type === "email" && a.email === b.email;
    case "files":
      return b.type === "files" && sameIds(a.fileIds, b.fileIds);
    case "relation":
      return b.type === "relation" && sameIds(a.pageIds, b.pageIds);
    case "contact":
      return b.type === "contact" && sameIds(a.contactIds, b.contactIds);
    case "formula":
    case "rollup":
      return b.type === a.type && sameResult(a.result, b.result);
    case "cell_formula":
      return b.type === "cell_formula" && a.expression === b.expression && sameResult(a.result, b.result);
  }
}

/** Deep equality of two formula/rollup results. */
export function sameResult(a: FormulaResult, b: FormulaResult): boolean {
  switch (a.kind) {
    case "number":
      return b.kind === "number" && a.number === b.number;
    case "text":
      return b.kind === "text" && a.text === b.text;
    case "boolean":
      return b.kind === "boolean" && a.value === b.value;
    case "date":
      return b.kind === "date" && a.date.start === b.date.start && (a.date.end ?? "") === (b.date.end ?? "");
    case "error":
      return b.kind === "error" && a.error === b.error && (a.message ?? "") === (b.message ?? "");
  }
}

/** Same set of cells with equal values. */
export function sameValues(a: Record<string, PropertyValue>, b: Record<string, PropertyValue>): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) {
    const va = a[k];
    const vb = b[k];
    if (va === undefined || vb === undefined || !sameValue(va, vb)) return false;
  }
  return true;
}

/**
 * The temporary row a server row is the echo of (realtime `row.created` can beat the REST answer): one of my
 * pending creates in the same database with the same cells. Returns its temporary id, if any.
 */
export function matchPendingRow(rows: Row[], row: Row, pendingIds: string[], meId: string): string | undefined {
  if (row.createdBy !== meId || rows.some((r) => r.id === row.id)) return undefined;
  const temp = rows.find((r) => pendingIds.includes(r.id) && r.databaseId === row.databaseId && sameValues(r.values, row.values));
  return temp?.id;
}

/** Replaces a temporary row by the server's row. */
export function replaceRow(rows: Row[], tempId: string, row: Row): Row[] {
  return upsertRow(rows.filter((r) => r.id !== tempId), row);
}

/** Applies a realtime event to a cached database (events for other databases return the cache unchanged). */
export function applyDbEvent(cache: DatabaseCache, ev: PersistentEvent): DatabaseCache {
  const id = cache.database.id;
  switch (ev.type) {
    case "database.updated":
      return ev.database.id === id ? { ...cache, database: ev.database } : cache;
    case "row.created":
    case "row.updated":
      return ev.row.databaseId === id ? { ...cache, rows: upsertRow(cache.rows, ev.row) } : cache;
    case "row.deleted": {
      if (ev.databaseId !== id) return cache;
      const rows = removeRow(cache.rows, ev.rowId);
      return rows === cache.rows ? cache : { ...cache, rows };
    }
    case "view.created":
    case "view.updated":
      return ev.view.databaseId === id ? { ...cache, views: upsertView(cache.views, ev.view) } : cache;
    case "view.deleted": {
      if (ev.databaseId !== id) return cache;
      const views = removeView(cache.views, ev.viewId);
      return views === cache.views ? cache : { ...cache, views };
    }
    default:
      return cache;
  }
}

/** The database a realtime event touches, if any. */
export function eventDatabaseId(ev: PersistentEvent): string | undefined {
  switch (ev.type) {
    case "database.updated":
      return ev.database.id;
    case "row.created":
    case "row.updated":
      return ev.row.databaseId;
    case "row.deleted":
    case "view.deleted":
      return ev.databaseId;
    case "view.created":
    case "view.updated":
      return ev.view.databaseId;
    default:
      return undefined;
  }
}
